import React from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  GruppertSkjema,
  OneWayInOpportunityCreateDto,
  OneWayInOpportunitySimpleV2Dto,
  OneWayInService,
} from 'src/api/v2';
import { useUser } from 'src/features/authorization';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import { ROUTES } from 'src/navigation';
import ToastContent from 'src/components/ToastContent/ToastContent';
import useAddAdvisor from './use-add-advisor';
import { useCluster } from 'src/features/cluster';
import { parseDateString } from 'src/utils/date';
import { ONE_WAY_IN_GROUPED_FORM } from 'src/constants/hook-keys';
import useCreateOpportunity from '../../hooks/use-create-opportunity';

const useHandleAddAdvisor = (
  opportunity: Pick<OneWayInOpportunitySimpleV2Dto, 'id' | 'oneWayInId' | 'assignedTo' | 'clusterId'>,
) => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { user } = useUser();
  const siteimprove = useSiteimprove();
  const location = useLocation();
  const { addAdvisorMutation } = useAddAdvisor(opportunity.oneWayInId);
  const queryClient = useQueryClient();
  const [service] = React.useState(() => new OneWayInService());
  const { cluster } = useCluster();
  const { createOpportunity } = useCreateOpportunity();

  const handleAddAdvisor = async (advisorId: string, sendEmailToAdvisor: boolean) => {
    let opportunityId = opportunity.id;

    // Create opportunity if it does not exist
    if (!opportunity.clusterId && !!cluster) {
      const formData = await queryClient.fetchQuery<GruppertSkjema>({
        queryKey: [ONE_WAY_IN_GROUPED_FORM, opportunity.oneWayInId],
        queryFn: () => service.getInquiry(opportunity.oneWayInId),
        staleTime: 1000 * 60 * 15,
      });
      if (formData) {
        const createDto: OneWayInOpportunityCreateDto = {
          clusterId: cluster.id,
          oneWayInId: formData.id!,
          title: formData.tittel ?? '',
          municipality: formData.prosjektkommune ?? '',
          customerName: formData.kontakt?.navn,
          organizationName: formData.organisasjon?.navn,
          organizationNumber: formData.organisasjon?.orgNr,
          submittedOn: formData.innsendtDato ? parseDateString(formData.innsendtDato) : undefined,
        };
        const created = await createOpportunity.mutateAsync(createDto);
        if (created) {
          opportunityId = created.id;
        }
      }
    }

    await addAdvisorMutation
      .mutateAsync({
        opportunityId: opportunityId,
        clusterUserId: advisorId,
        emailDto: sendEmailToAdvisor ? { handlersName: user?.displayName ?? '' } : undefined,
      })
      .then(() => {
        if (location.pathname === ROUTES.ONE_WAY_IN.BASE) {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.ASSIGN_ADVISOR,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.labels.SUCCESS,
          );
        } else if (
          location.pathname
            .replace(/%7C/g, '|')
            .includes(ROUTES.ONE_WAY_IN.DETAILS.BASE(opportunity.oneWayInId))
        ) {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_DETAILS.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_DETAILS.actions.ASSIGN_ADVISOR,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_DETAILS.labels.SUCCESS,
          );
        }
        toast(
          <ToastContent
            title={tOneWayIn('opportunity.addAdvisor.toastSuccess.title')}
            text={tOneWayIn('opportunity.addAdvisor.toastSuccess.info')}
            success={true}
          />,
          { toastId: 'AdvisorAdded', autoClose: false, position: 'bottom-center' },
        );
        if (sendEmailToAdvisor) {
          toast(
            <ToastContent
              title={tOneWayIn('opportunity.addAdvisor.email.toastSuccess.title')}
              text={tOneWayIn('opportunity.addAdvisor.email.toastSuccess.info')}
              success={true}
            />,
            { toastId: 'AdvisorAddedEmail', autoClose: false, position: 'bottom-center' },
          );
        }
      })
      .catch(() => {
        if (location.pathname === ROUTES.ONE_WAY_IN.BASE) {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.ASSIGN_ADVISOR,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.labels.ERROR,
          );
        } else if (
          location.pathname
            .replace(/%7C/g, '|')
            .includes(ROUTES.ONE_WAY_IN.DETAILS.BASE(opportunity.oneWayInId))
        ) {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_DETAILS.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_DETAILS.actions.ASSIGN_ADVISOR,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_DETAILS.labels.ERROR,
          );
        }
        toast(
          <ToastContent
            title={tOneWayIn('opportunity.addAdvisor.toastError.title')}
            text={tOneWayIn('opportunity.addAdvisor.toastError.info')}
            success={false}
          />,
          { toastId: 'AdvisorNotAdded', autoClose: false, position: 'bottom-center' },
        );
      });
  };

  return {
    handleAddAdvisor,
    isLoading: addAdvisorMutation.isLoading,
  };
};

export default useHandleAddAdvisor;
